import type { ThreadError } from '@fruitful/types';
import type { ClientScoreRowData } from '@mastra/client-js';
import { useEffect, useState } from 'react';
import {
    BodyLargeEmphasized,
    BodySmallRegular,
    CheckboxWithLabel,
    Input,
    MultilineInput,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import {
    toEvalFeedbackErrors,
    toEvalFeedbackScores,
    useCreateThreadFeedback,
} from '../hooks/useCreateThreadFeedback';

export const ThreadFeedbackModal = ({
    isOpen,
    onClose,
    threadId,
    agentId,
    scores,
    errors,
}: {
    isOpen: boolean;
    onClose: () => void;
    threadId: string;
    agentId: string;
    scores: ClientScoreRowData[];
    errors: ThreadError[];
}) => {
    const toast = useToastController();
    const { mutateAsync, isPending } = useCreateThreadFeedback();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [assignToBot, setAssignToBot] = useState(true);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setTitle('');
        setDescription('');
        setAssignToBot(true);
    }, [isOpen]);

    const canSubmit =
        title.trim() !== '' && description.trim() !== '' && !isPending;

    const handleSubmit = async () => {
        if (!canSubmit) {
            return;
        }

        const scoreSnapshot = toEvalFeedbackScores(scores, agentId);
        const errorSnapshot = toEvalFeedbackErrors(errors);

        try {
            const result = await mutateAsync({
                body: {
                    title: title.trim(),
                    description: description.trim(),
                    threadId,
                    agentId,
                    assignToBot,
                    ...(scoreSnapshot.length > 0
                        ? { scores: scoreSnapshot }
                        : {}),
                    ...(errorSnapshot.length > 0
                        ? { errors: errorSnapshot }
                        : {}),
                },
            });

            onClose();
            toast.show('Feedback filed', {
                ...MutationSuccessToastOptions,
                duration: 8000,
                ...(result.url === ''
                    ? { description: result.identifier }
                    : {
                          button: (
                              <a
                                  href={result.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                      color: '#007bff',
                                      fontWeight: 600,
                                  }}
                              >
                                  {result.identifier}
                              </a>
                          ),
                      }),
            });
        } catch (error) {
            toast.show(
                'Could not file feedback',
                MutationErrorToastOptions(error),
            );
        }
    };

    return (
        <GroveSimpleModal isOpen={isOpen} onClose={onClose} hasCloseButton>
            <YStack gap={16} padding={16} width={560}>
                <YStack gap={4}>
                    <BodyLargeEmphasized>Give feedback</BodyLargeEmphasized>
                    <BodySmallRegular color="$gray1000">
                        Files a Linear issue for this thread. Scores and errors
                        currently on the page are included.
                    </BodySmallRegular>
                </YStack>
                <Input
                    label="Title"
                    value={title}
                    onChangeText={setTitle}
                    disabled={isPending}
                    placeholder="What should be reviewed"
                />
                <MultilineInput
                    label="Description"
                    value={description}
                    onChangeText={setDescription}
                    disabled={isPending}
                    numberOfLines={5}
                    placeholder="What looks wrong, and what you expected"
                />
                <YStack gap={4}>
                    <CheckboxWithLabel
                        id="eval-feedback-assign-to-bot"
                        label="Assign to bot"
                        checked={assignToBot}
                        disabled={isPending}
                        onPress={() => {
                            if (isPending) {
                                return;
                            }
                            setAssignToBot((current) => !current);
                        }}
                    />
                    <BodySmallRegular color="$gray1000" paddingLeft={4}>
                        Assigns the issue to the review bot.
                    </BodySmallRegular>
                </YStack>
                <XStack justifyContent="flex-end" gap={8}>
                    <Button
                        variant="tertiary"
                        size="small"
                        disabled={isPending}
                        onPress={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="small"
                        disabled={!canSubmit}
                        onPress={() => {
                            void handleSubmit();
                        }}
                    >
                        {isPending ? 'Submitting' : 'Submit feedback'}
                    </Button>
                </XStack>
            </YStack>
        </GroveSimpleModal>
    );
};
